import type { ReactNode } from "react";
import { Button } from "./ui";

/**
 * The bar that appears once a row is ticked: how many, a way to un-tick them
 * all, and the one thing a selection is for.
 *
 * Pinned to the bottom of the viewport rather than put in a card header,
 * because a selection spans cards — an operator ticking two apps and a
 * database on a tenant's page should see one count and one button, not a
 * button per table each claiming the whole selection. It stays in reach while
 * the page scrolls, which a header does not.
 *
 * On a phone the actions wrap under the count rather than squeezing it out: a
 * queue whose bar carries four moves — Cases does — is still worked from one
 * during an incident.
 */
export function SelectionBar({
  count,
  noun,
  onClear,
  action,
  children,
}: {
  count: number;
  noun: string;
  onClear: () => void;
  /** The primary thing to do with the selection. */
  action: ReactNode;
  /** Anything else worth saying beside the count — a caveat, a breakdown. */
  children?: ReactNode;
}) {
  if (count === 0) return null;

  return (
    <>
      {/* In flow, so the page grows by the bar's height and nothing at the
          bottom — the last card's own buttons — is left underneath it. Taller
          on a phone, where the actions may take a second line. */}
      <div aria-hidden="true" className="h-32 sm:h-20" />
      <div
        role="region"
        aria-label="Selection"
        className="rise-in fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-30 flex w-[min(40rem,calc(100vw-2rem))] flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line-strong bg-surface/95 px-4 py-2.5 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.8)] backdrop-blur-md"
      >
        <span className="font-mono text-xs tabular-nums text-fg">
          {count} {noun}
          {count === 1 ? "" : "s"} selected
        </span>
        {children && <span className="min-w-0 flex-1 truncate text-xs text-fg-subtle">{children}</span>}
        {/* Its own item rather than the first of the actions, so on a phone
            it stays up with the count it clears and the actions get a line
            of their own — which they may wrap within, never past the edge. */}
        <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={onClear}>
          Clear
        </Button>
        <span className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2 max-sm:ml-auto">{action}</span>
      </div>
    </>
  );
}
